<template>
  <v-dialog
    :model-value="modelValue"
    max-width="820"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card tk-dialog">
      <div class="dialog-header tk-dialog__header">
        <div class="tk-dialog__header-main">
          <div class="dialog-header__icon tk-dialog__icon-wrap">
            <v-icon size="22">mdi-playlist-edit</v-icon>
          </div>
          <div class="tk-dialog__head-copy">
            <span class="dialog-header__title">Bulk update status</span>
            <span class="tk-dialog__subtitle">
              Apply a lead status and comments while the campaign is Active.
              Selected leads update immediately (max 500 per request). Entire
              campaign runs in the background.
            </span>
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          size="small"
          class="dialog-close-btn"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body tk-dialog__body">
        <div class="bu-modes" role="tablist" aria-label="Update scope">
          <button
            type="button"
            class="bu-modes__tab"
            :class="{ 'is-active': mode === 'selected' }"
            :disabled="saving"
            @click="mode = 'selected'"
          >
            Selected leads
          </button>
          <button
            type="button"
            class="bu-modes__tab"
            :class="{ 'is-active': mode === 'all' }"
            :disabled="saving"
            @click="mode = 'all'"
          >
            Entire campaign
          </button>
        </div>

        <div class="tk-dialog__field">
          <label class="tk-dialog__label">
            Status <span class="tk-dialog__required">*</span>
          </label>
          <v-select
            v-model="requestStatus"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            placeholder="Select status"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            class="tk-dialog__input"
            :disabled="saving"
          />
        </div>

        <div class="tk-dialog__field">
          <label class="tk-dialog__label">
            Comments <span class="tk-dialog__required">*</span>
          </label>
          <v-textarea
            v-model="comments"
            rows="3"
            auto-grow
            placeholder="Note explaining this status change…"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            class="tk-dialog__input"
            :disabled="saving"
          />
        </div>

        <div class="tk-dialog__field">
          <div class="tk-dialog__filters-head">
            <label class="tk-dialog__label">
              {{ mode === 'selected' ? 'Advanced filters' : 'Optional filters' }}
            </label>
            <v-btn
              variant="text"
              size="small"
              class="text-none"
              :disabled="saving"
              @click="addFilterRow"
            >
              Add filter
            </v-btn>
          </div>
          <p class="tk-dialog__hint">
            <template v-if="mode === 'selected'">
              Filter locally, then update. Matching leads are selected automatically.
              Same field → OR. Different fields → AND.
            </template>
            <template v-else>
              Leave empty to update every lead in the campaign. Filters are applied on
              the server (same field → OR, different fields → AND).
            </template>
          </p>

          <div v-if="mode === 'selected'" class="bu-quick bu-quick--wide">
            <v-select
              v-model="assignmentFilter"
              :items="assignmentOptions"
              item-title="title"
              item-value="value"
              label="Assignment"
              variant="outlined"
              density="compact"
              hide-details
              class="tk-dialog__input"
              :disabled="saving"
            />
            <v-select
              v-model="statusFilter"
              :items="leadStatusFilterOptions"
              item-title="title"
              item-value="value"
              label="Status"
              variant="outlined"
              density="compact"
              hide-details
              class="tk-dialog__input"
              :disabled="saving"
            />
            <v-text-field
              v-model="search"
              placeholder="Search name, phone, email…"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              class="tk-dialog__input"
              :disabled="saving"
            />
          </div>

          <div v-if="advancedFilters.length" class="bu-filters">
            <div
              v-for="(filter, index) in advancedFilters"
              :key="filter.id"
              class="bu-filter-row"
              :class="{ 'bu-filter-row--range': isRangeOperator(filter.operator) }"
            >
              <v-select
                v-model="filter.field"
                :items="filterFieldOptions"
                item-title="label"
                item-value="key"
                placeholder="Field"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="onFilterFieldChange(filter)"
              />
              <v-select
                v-model="filter.operator"
                :items="operatorsForRow(filter)"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="onFilterOperatorChange(filter)"
              />

              <div v-if="isRangeOperator(filter.operator)" class="bu-filter-range">
                <v-text-field
                  v-if="filterRowKind(filter) === 'date'"
                  :model-value="getFilterDateInputValue(getFilterRangeMin(filter))"
                  type="date"
                  placeholder="From"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tk-dialog__input"
                  :disabled="saving"
                  @update:model-value="setFilterRangeMin(filter, $event)"
                />
                <v-text-field
                  v-else
                  :model-value="getFilterRangeMin(filter)"
                  type="number"
                  placeholder="Min"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tk-dialog__input"
                  :disabled="saving"
                  @update:model-value="setFilterRangeMin(filter, $event)"
                />
                <v-text-field
                  v-if="filterRowKind(filter) === 'date'"
                  :model-value="getFilterDateInputValue(getFilterRangeMax(filter))"
                  type="date"
                  placeholder="To"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tk-dialog__input"
                  :disabled="saving"
                  @update:model-value="setFilterRangeMax(filter, $event)"
                />
                <v-text-field
                  v-else
                  :model-value="getFilterRangeMax(filter)"
                  type="number"
                  placeholder="Max"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tk-dialog__input"
                  :disabled="saving"
                  @update:model-value="setFilterRangeMax(filter, $event)"
                />
              </div>
              <v-select
                v-else-if="isMultiOperator(filter.operator) && usesValueSelect(filter)"
                :model-value="filter.values"
                :items="valueOptionsForFilter(filter)"
                multiple
                chips
                closable-chips
                placeholder="Select values"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="filter.values = $event || []"
              />
              <v-combobox
                v-else-if="isMultiOperator(filter.operator)"
                :model-value="filter.values"
                multiple
                chips
                closable-chips
                placeholder="Values"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="filter.values = $event || []"
              />
              <v-text-field
                v-else-if="filterRowKind(filter) === 'date'"
                :model-value="getFilterDateInputValue(getFilterSingleValue(filter))"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />
              <v-text-field
                v-else-if="filterRowKind(filter) === 'number'"
                :model-value="getFilterSingleValue(filter)"
                type="number"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />
              <v-select
                v-else-if="usesValueSelect(filter)"
                :model-value="getFilterSingleValue(filter)"
                :items="valueOptionsForFilter(filter)"
                placeholder="Select value"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="tk-dialog__input"
                :disabled="saving"
                no-data-text="No values found for this field"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />
              <v-text-field
                v-else
                :model-value="getFilterSingleValue(filter)"
                placeholder="Value"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />

              <v-btn
                icon
                variant="text"
                size="small"
                :disabled="saving"
                @click="removeFilterRow(index)"
              >
                <v-icon size="18">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <template v-if="mode === 'selected'">
          <div class="tk-dialog__field">
            <div class="tk-dialog__filters-head">
              <label class="tk-dialog__label">
                Leads
                <span class="tk-dialog__label-hint">
                  {{ visibleLeads.length }} match · {{ selectedIds.length }} selected
                </span>
              </label>
              <div class="bu-select-actions">
                <v-btn
                  variant="text"
                  size="small"
                  class="text-none"
                  :disabled="saving || !visibleLeads.length"
                  @click="selectAllVisible"
                >
                  Select all matches
                </v-btn>
                <v-btn
                  variant="text"
                  size="small"
                  class="text-none"
                  :disabled="saving || !visibleLeads.length"
                  @click="selectUnassigned"
                >
                  Select unassigned
                </v-btn>
                <v-btn
                  variant="text"
                  size="small"
                  class="text-none"
                  :disabled="saving || !selectedIds.length"
                  @click="clearSelection"
                >
                  Clear
                </v-btn>
              </div>
            </div>

            <p v-if="selectedIds.length > 500" class="tk-dialog__hint">
              Will send in chunks of 500
            </p>

            <div class="bu-lead-list">
              <label
                v-for="lead in visibleLeads"
                :key="lead.requestId"
                class="bu-lead-row"
              >
                <input
                  type="checkbox"
                  :checked="selectedIds.includes(String(lead.requestId))"
                  :disabled="saving"
                  @change="
                    toggleLead(lead.requestId, $event.target.checked)
                  "
                />
                <span class="bu-lead-row__copy">
                  <strong>{{ displayName(lead) }}</strong>
                  <small>
                    {{ lead.phone || lead.phoneRaw || '—' }}
                    · {{ lead.statusLabel || lead.status || '—' }}
                    <template v-if="lead.leadScore != null">
                      · Score {{ lead.leadScore }}
                    </template>
                  </small>
                </span>
              </label>
              <p v-if="!visibleLeads.length" class="tk-dialog__empty">
                No leads match the current filters.
              </p>
            </div>
          </div>
        </template>

        <template v-else>
          <p v-if="jobPhase !== 'idle'" class="bu-job">
            <v-progress-circular indeterminate size="16" width="2" />
            {{ jobPhaseLabel }}
          </p>
        </template>

        <div v-if="resultErrors.length" class="tk-dialog__unmatched">
          <p class="tk-dialog__section-title">Failed rows</p>
          <div class="tk-dialog__unmatched-table">
            <div class="tk-dialog__unmatched-head">
              <span>Lead</span>
              <span>Status</span>
              <span>Error</span>
            </div>
            <div
              v-for="(row, idx) in resultErrors"
              :key="`${row.request_id}-${idx}`"
              class="tk-dialog__unmatched-row"
            >
              <span>{{ failedRowLabel(row) }}</span>
              <span>{{ row.request_status || '—' }}</span>
              <span>{{ row.error || '—' }}</span>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="dialog-footer tk-dialog__footer">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-none tk-dialog__cancel"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          class="text-none btn-primary-cta tk-dialog__save"
          rounded="lg"
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ submitLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import {
  bulkUpdateAllCampaignLeadStatus,
  bulkUpdateCampaignLeadStatus,
  pollCampaignAssignmentUploadStatus,
} from '@/api/campaignsApi'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { businessQueryStatusLabel } from '@/utils/businessQueries'
import {
  buildCampaignFilterFieldOptions,
  buildFilterValueOptions,
  campaignFilterFieldKind,
  createEmptyCampaignFilter,
  findCampaignFilterFieldDef,
  getFilterDateInputValue,
  getFilterRangeMax,
  getFilterRangeMin,
  getFilterSingleValue,
  isSelectFilterField,
  leadMatchesCampaignFilters,
  normalizeCampaignFiltersForApi,
  operatorsForFilterField,
  setFilterRangeMax,
  setFilterRangeMin,
  setFilterSingleValue,
} from '@/utils/campaignFilters'
import { humanDisplayLabel } from '@/utils/leadFields'

defineOptions({ name: 'BulkUpdateCampaignLeadStatusDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  campaignId: { type: String, default: '' },
  leads: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  contexts: { type: Array, default: () => [] },
  statusOptions: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'saved', 'error'])

const mode = ref('selected')
const requestStatus = ref('')
const comments = ref('')
const search = ref('')
const assignmentFilter = ref('any')
const statusFilter = ref('')
const selectedIds = ref([])
const advancedFilters = ref([])
const saving = ref(false)
const resultErrors = ref([])
const jobPhase = ref('idle')
const autoSelectMatches = ref(true)

const assignmentOptions = [
  { value: 'any', title: 'Any assignment' },
  { value: 'unassigned', title: 'Unassigned only' },
  { value: 'assigned', title: 'Assigned only' },
]

const statusOptions = computed(() => {
  if (Array.isArray(props.statusOptions) && props.statusOptions.length) {
    return props.statusOptions
  }
  const seen = new Set()
  const options = []
  for (const context of Array.isArray(props.contexts) ? props.contexts : []) {
    const statuses = Array.isArray(context?.lead_statuses)
      ? context.lead_statuses
      : Array.isArray(context?.leadStatuses)
        ? context.leadStatuses
        : []
    for (const status of statuses) {
      const value = String(
        typeof status === 'string' ? status : status?.value ?? status?.status ?? '',
      )
        .trim()
        .toUpperCase()
      if (!value || seen.has(value)) continue
      seen.add(value)
      options.push({
        value,
        title: businessQueryStatusLabel(value),
      })
    }
  }
  for (const lead of Array.isArray(props.leads) ? props.leads : []) {
    const value = String(lead?.status ?? '').trim().toUpperCase()
    if (!value || seen.has(value)) continue
    seen.add(value)
    options.push({ value, title: businessQueryStatusLabel(value) })
  }
  return options.sort((a, b) => a.title.localeCompare(b.title))
})

const leadStatusFilterOptions = computed(() => {
  const seen = new Set()
  for (const lead of Array.isArray(props.leads) ? props.leads : []) {
    const status = String(lead?.status ?? '').trim().toUpperCase()
    if (status) seen.add(status)
  }
  return [
    { value: '', title: 'All statuses' },
    ...[...seen].sort().map((status) => ({
      value: status,
      title: businessQueryStatusLabel(status),
    })),
  ]
})

const filterFieldOptions = computed(() =>
  buildCampaignFilterFieldOptions(props.contexts),
)

const visibleLeads = computed(() => {
  const q = String(search.value ?? '').trim().toLowerCase()
  const status = String(statusFilter.value ?? '').trim().toUpperCase()
  const list = Array.isArray(props.leads) ? props.leads : []

  return list.filter((lead) => {
    if (mode.value === 'selected') {
      const assigned = Boolean(String(lead.assignedAgentId ?? '').trim())
      if (assignmentFilter.value === 'unassigned' && assigned) return false
      if (assignmentFilter.value === 'assigned' && !assigned) return false
    }

    if (status && String(lead.status ?? '').trim().toUpperCase() !== status) {
      return false
    }

    if (
      !leadMatchesCampaignFilters(
        lead,
        advancedFilters.value,
        filterFieldOptions.value,
      )
    ) {
      return false
    }

    if (!q) return true
    return [
      displayName(lead),
      lead.phone,
      lead.phoneRaw,
      lead.email,
      lead.statusLabel,
      lead.status,
      lead.industry,
      lead.typeOfBusiness,
      humanDisplayLabel(lead.assignedAgentName),
    ].some((value) => String(value ?? '').toLowerCase().includes(q))
  })
})

const canSubmit = computed(() => {
  if (saving.value) return false
  if (!String(props.campaignId ?? '').trim()) return false
  if (!String(requestStatus.value ?? '').trim()) return false
  if (!String(comments.value ?? '').trim()) return false
  if (mode.value === 'selected') return selectedIds.value.length > 0
  return true
})

const submitLabel = computed(() => {
  if (mode.value === 'all') return 'Update all matching'
  const n = selectedIds.value.length
  return n ? `Update ${n}` : 'Update selected'
})

const jobPhaseLabel = computed(() => {
  if (jobPhase.value === 'starting') return 'Starting bulk update…'
  if (jobPhase.value === 'polling') return 'Applying status updates…'
  return ''
})

function displayName(lead) {
  return (
    String(lead?.contactName || lead?.name || lead?.requestId || 'Lead').trim() ||
    'Lead'
  )
}

const leadsByRequestId = computed(() => {
  const map = new Map()
  for (const lead of Array.isArray(props.leads) ? props.leads : []) {
    const id = String(lead?.requestId ?? '').trim()
    if (id) map.set(id, lead)
  }
  return map
})

function failedRowLabel(row) {
  const id = String(row?.request_id ?? '').trim()
  const lead = id ? leadsByRequestId.value.get(id) : null
  if (!lead) return id || '—'
  const name = displayName(lead)
  const phone = lead.phone || lead.phoneRaw
  return phone ? `${name} · ${phone}` : name
}

function operatorsForRow(filter) {
  const def = findCampaignFilterFieldDef(filterFieldOptions.value, filter.field)
  return operatorsForFilterField(def)
}

function filterRowKind(filter) {
  const def = findCampaignFilterFieldDef(filterFieldOptions.value, filter.field)
  return campaignFilterFieldKind(def)
}

function filterFieldDef(filter) {
  return findCampaignFilterFieldDef(filterFieldOptions.value, filter.field)
}

function usesValueSelect(filter) {
  return isSelectFilterField(filterFieldDef(filter))
}

function valueOptionsForFilter(filter) {
  return buildFilterValueOptions(filterFieldDef(filter), props.leads)
}

function isRangeOperator(operator) {
  return String(operator ?? '').toUpperCase() === 'BETWEEN'
}

function isMultiOperator(operator) {
  return String(operator ?? '').toUpperCase() === 'IN'
}

function onFilterFieldChange(filter) {
  const ops = operatorsForRow(filter)
  if (!ops.some((op) => op.value === filter.operator)) {
    filter.operator = ops[0]?.value || 'EQUALS'
  }
  filter.values = []
}

function onFilterOperatorChange(filter) {
  if (isRangeOperator(filter.operator)) {
    filter.values = [getFilterRangeMin(filter), getFilterRangeMax(filter)]
    return
  }
  if (isMultiOperator(filter.operator)) {
    filter.values = Array.isArray(filter.values) ? filter.values : []
    return
  }
  const single = getFilterSingleValue(filter)
  filter.values = single === '' || single == null ? [] : [single]
}

function addFilterRow() {
  advancedFilters.value = [...advancedFilters.value, createEmptyCampaignFilter()]
}

function removeFilterRow(index) {
  const next = advancedFilters.value.filter((_, i) => i !== index)
  advancedFilters.value = next
}

function selectAllVisible() {
  autoSelectMatches.value = true
  selectedIds.value = visibleLeads.value
    .map((lead) => String(lead.requestId ?? '').trim())
    .filter(Boolean)
}

function selectUnassigned() {
  autoSelectMatches.value = false
  selectedIds.value = visibleLeads.value
    .filter((lead) => !String(lead.assignedAgentId ?? '').trim())
    .map((lead) => String(lead.requestId ?? '').trim())
    .filter(Boolean)
}

function clearSelection() {
  autoSelectMatches.value = false
  selectedIds.value = []
}

function pruneSelectionToVisible() {
  const visible = new Set(
    visibleLeads.value
      .map((lead) => String(lead.requestId ?? '').trim())
      .filter(Boolean),
  )
  selectedIds.value = selectedIds.value.filter((id) => visible.has(id))
}

function toggleLead(requestId, checked) {
  const id = String(requestId ?? '').trim()
  if (!id) return
  autoSelectMatches.value = false
  if (checked) {
    if (!selectedIds.value.includes(id)) {
      selectedIds.value = [...selectedIds.value, id]
    }
    return
  }
  selectedIds.value = selectedIds.value.filter((item) => item !== id)
}

function close() {
  if (saving.value) return
  emit('update:modelValue', false)
}

async function submitSelected() {
  const status = String(requestStatus.value ?? '').trim().toUpperCase()
  const note = String(comments.value ?? '').trim()
  const updates = selectedIds.value.map((request_id) => ({
    request_id,
    request_status: status,
    comments: note,
  }))

  const response = await bulkUpdateCampaignLeadStatus({
    task_id: props.campaignId,
    updates,
  })
  resultErrors.value = Array.isArray(response.errors) ? response.errors : []

  if (response.status === 'ERROR' && !response.succeeded) {
    emit('error', response.message || 'Bulk status update failed')
    return false
  }

  emit('saved', {
    mode: 'selected',
    message: response.message,
    succeeded: response.succeeded,
    failed: response.failed,
    errors: resultErrors.value,
    requestStatus: status,
    comments: note,
    requestIds: selectedIds.value.slice(),
  })
  return !resultErrors.value.length
}

async function submitAll() {
  const status = String(requestStatus.value ?? '').trim().toUpperCase()
  const note = String(comments.value ?? '').trim()
  const filters = normalizeCampaignFiltersForApi(
    advancedFilters.value,
    filterFieldOptions.value,
  )

  jobPhase.value = 'starting'
  const response = await bulkUpdateAllCampaignLeadStatus({
    task_id: props.campaignId,
    request_status: status,
    comments: note,
    filters,
  })

  if (!isApiSuccess(response)) {
    emit('error', response?.message || 'Bulk status update failed')
    return false
  }

  const root =
    response?.data && typeof response.data === 'object' ? response.data : response
  const uploadId = String(root?.upload_id ?? root?.uploadId ?? '').trim()
  if (!uploadId) {
    emit(
      'error',
      response?.message ||
        'Bulk update started, but upload_id was missing — refresh the campaign shortly.',
    )
    emit('saved', {
      mode: 'all',
      message: response?.message,
      requestStatus: status,
      comments: note,
      uploadId: '',
    })
    return true
  }

  jobPhase.value = 'polling'
  const poll = await pollCampaignAssignmentUploadStatus(uploadId)
  if (poll.timedOut) {
    emit('error', poll.message)
    emit('saved', {
      mode: 'all',
      message: poll.message,
      requestStatus: status,
      comments: note,
      uploadId,
      pending: true,
    })
    return true
  }
  if (poll.status === 'FAILED') {
    emit('error', poll.message || 'Bulk status update failed')
    return false
  }

  const matched =
    poll.matchedCount != null ? ` Updated ${poll.matchedCount} lead(s).` : ''
  emit('saved', {
    mode: 'all',
    message: poll.message || `Bulk status update completed.${matched}`,
    requestStatus: status,
    comments: note,
    uploadId,
  })
  return true
}

async function submit() {
  if (!canSubmit.value) return
  saving.value = true
  resultErrors.value = []
  jobPhase.value = 'idle'
  try {
    const ok =
      mode.value === 'selected' ? await submitSelected() : await submitAll()
    if (ok) emit('update:modelValue', false)
  } finally {
    saving.value = false
    jobPhase.value = 'idle'
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    mode.value = 'selected'
    requestStatus.value = ''
    comments.value = ''
    search.value = ''
    assignmentFilter.value = 'any'
    statusFilter.value = ''
    advancedFilters.value = []
    resultErrors.value = []
    jobPhase.value = 'idle'
    autoSelectMatches.value = true
    queueMicrotask(() => selectAllVisible())
  },
)

watch(mode, () => {
  advancedFilters.value = []
  assignmentFilter.value = 'any'
  statusFilter.value = ''
  search.value = ''
  if (mode.value === 'selected') {
    autoSelectMatches.value = true
    queueMicrotask(() => selectAllVisible())
  } else {
    selectedIds.value = []
  }
})

watch(visibleLeads, () => {
  if (mode.value !== 'selected') return
  if (autoSelectMatches.value) {
    selectAllVisible()
    return
  }
  pruneSelectionToVisible()
})
</script>

<style scoped>
.tk-dialog :deep(.dialog-header.tk-dialog__header) {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.tk-dialog__header-main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
}

.tk-dialog :deep(.dialog-header__icon.tk-dialog__icon-wrap) {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: rgba(79, 70, 229, 0.12);
  color: #4f46e5;
  flex-shrink: 0;
}

.tk-dialog__head-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.tk-dialog__subtitle {
  font-size: 0.78rem;
  line-height: 1.4;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog :deep(.dialog-body.tk-dialog__body) {
  padding: 20px 24px 10px;
}

.tk-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 18px;
  min-width: 0;
}

.tk-dialog__label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog__required {
  color: #dc2626;
}

.tk-dialog__hint {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog__filters-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.tk-dialog__empty {
  margin: 0;
  padding: 1.25rem;
  text-align: center;
  font-size: 0.82rem;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog__section-title {
  margin: 0 0 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog__unmatched {
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
}

.tk-dialog__unmatched-table {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  overflow: hidden;
  font-size: 0.75rem;
}

.tk-dialog__unmatched-head,
.tk-dialog__unmatched-row {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 1.6fr;
  gap: 8px;
  padding: 0.55rem 0.7rem;
}

.tk-dialog__unmatched-head {
  background: rgba(148, 163, 184, 0.1);
  font-weight: 650;
}

.tk-dialog__unmatched-row + .tk-dialog__unmatched-row {
  border-top: 1px solid rgba(148, 163, 184, 0.12);
}

.bu-modes {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  margin-bottom: 1rem;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.05);
}

.bu-modes__tab {
  appearance: none;
  border: 0;
  padding: 0.42rem 0.9rem;
  border-radius: 999px;
  background: transparent;
  color: var(--y-text-muted, #64748b);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 650;
  cursor: pointer;
}

.bu-modes__tab.is-active {
  background: #fff;
  color: #4f46e5;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.bu-modes__tab:disabled {
  opacity: 0.55;
  cursor: default;
}

.tk-dialog__label-hint {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  opacity: 0.75;
}

.bu-quick {
  display: grid;
  grid-template-columns: minmax(0, 180px) minmax(0, 1fr);
  gap: 8px;
  margin-bottom: 8px;
}

.bu-quick--wide {
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.3fr);
}

.bu-filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding: 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.04);
}

.bu-filter-row {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1.4fr) 36px;
  gap: 8px;
  align-items: center;
}

.bu-filter-row--range {
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1.4fr) 36px;
}

.bu-filter-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.bu-select-actions {
  display: flex;
  gap: 2px;
}

.bu-lead-list {
  max-height: 240px;
  overflow: auto;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  padding: 0.35rem;
}

.bu-lead-row {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 0.65rem;
  cursor: pointer;
}

.bu-lead-row:hover {
  background: rgba(79, 70, 229, 0.06);
}

.bu-lead-row__copy {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.bu-lead-row__copy strong {
  font-size: 0.82rem;
  font-weight: 650;
}

.bu-lead-row__copy small {
  font-size: 0.72rem;
  color: var(--y-text-muted, #64748b);
}

.bu-job {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  color: var(--y-text-muted, #64748b);
}

@media (max-width: 700px) {
  .bu-quick,
  .bu-quick--wide,
  .bu-filter-row {
    grid-template-columns: 1fr;
  }
}
</style>
